import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef } from 'react';
import { firebaseAuth } from '@/lib/firebase';
import {
  Play, CheckCircle2, XCircle, Loader2, ChevronDown,
  ChevronRight, RefreshCw, AlertTriangle,
} from 'lucide-react';


export const Route = createFileRoute('/_auth/settings/api-tester')({ component: ApiTesterPage });

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

// ── Endpoint catalogue ────────────────────────────────────────────────────────

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

interface Endpoint {
  id: string;
  label: string;
  method: HttpMethod;
  path: string;
  group: string;
  body?: Record<string, unknown>;
  /** If true, skip actual request (e.g. destructive endpoints) */
  skip?: boolean;
  note?: string;
}

const ENDPOINTS: Endpoint[] = [
  // ── Health ─────────────────────────────────────────────────────────────────
  { id: 'health',            group: 'Health',           method: 'GET',    path: '/health',                                                label: 'Health check' },

  // ── Auth ───────────────────────────────────────────────────────────────────
  { id: 'me',                group: 'Auth',             method: 'GET',    path: '/v1/me',                                                 label: 'Current user (me)' },

  // ── Admin — Tenants ────────────────────────────────────────────────────────
  { id: 'admin-tenants',     group: 'Admin – Tenants',  method: 'GET',    path: '/v1/admin/tenants',                                     label: 'List tenants' },
  { id: 'admin-users',       group: 'Admin – Users',    method: 'GET',    path: '/v1/admin/users',                                       label: 'List users' },
  { id: 'admin-partners',    group: 'Admin – Partners', method: 'GET',    path: '/v1/admin/partners',                                    label: 'List partners' },
  { id: 'admin-audit',       group: 'Admin – Audit',    method: 'GET',    path: '/v1/admin/audit-log',                                   label: 'Audit log' },

  // ── Agents ─────────────────────────────────────────────────────────────────
  { id: 'agents',            group: 'Agents',           method: 'GET',    path: '/v1/agents',                                            label: 'List agents' },
  { id: 'agent-providers',   group: 'Agents',           method: 'GET',    path: '/v1/agents/model-providers/list',                      label: 'Model providers' },

  // ── Conversations ──────────────────────────────────────────────────────────
  { id: 'conversations',     group: 'Conversations',    method: 'GET',    path: '/v1/conversations',                                     label: 'List conversations' },
  { id: 'handoffs',          group: 'Conversations',    method: 'GET',    path: '/v1/handoffs',                                          label: 'List handoffs' },

  // ── Skills ─────────────────────────────────────────────────────────────────
  { id: 'skills',            group: 'Skills',           method: 'GET',    path: '/v1/skills',                                            label: 'List skills' },

  // ── Integrations ───────────────────────────────────────────────────────────
  { id: 'integrations',      group: 'Integrations',     method: 'GET',    path: '/v1/integrations',                                      label: 'List integrations' },
  { id: 'tools',             group: 'Integrations',     method: 'GET',    path: '/v1/tools',                                             label: 'List tools' },

  // ── Knowledge ──────────────────────────────────────────────────────────────
  { id: 'knowledge-sources', group: 'Knowledge',        method: 'GET',    path: '/v1/knowledge/sources',                                 label: 'List knowledge sources' },

  // ── Approvals ──────────────────────────────────────────────────────────────
  { id: 'approvals',         group: 'Approvals',        method: 'GET',    path: '/v1/approvals',                                         label: 'List approval requests' },

  // ── Guardrails ─────────────────────────────────────────────────────────────
  { id: 'guardrails',        group: 'Guardrails',       method: 'GET',    path: '/v1/guardrails',                                        label: 'List guardrails' },

  // ── Workflows ──────────────────────────────────────────────────────────────
  { id: 'workflows',         group: 'Workflows',        method: 'GET',    path: '/v1/workflows',                                         label: 'List workflows' },

  // ── Triggers ───────────────────────────────────────────────────────────────
  { id: 'triggers',          group: 'Triggers',         method: 'GET',    path: '/v1/triggers',                                          label: 'List triggers' },

  // ── HITL ───────────────────────────────────────────────────────────────────
  { id: 'hitl',              group: 'HITL',             method: 'GET',    path: '/v1/hitl',                                              label: 'List HITL requests' },

  // ── Settings ───────────────────────────────────────────────────────────────
  { id: 'api-keys',          group: 'Settings',         method: 'GET',    path: '/v1/settings/api-keys',                                label: 'API keys' },
  { id: 'alerts',            group: 'Settings',         method: 'GET',    path: '/v1/settings/alerts',                                  label: 'Alerts' },
  { id: 'voice-numbers',     group: 'Settings',         method: 'GET',    path: '/v1/settings/voice-numbers',                           label: 'Voice numbers' },

  // ── Teams ──────────────────────────────────────────────────────────────────
  { id: 'teams',             group: 'Teams',            method: 'GET',    path: '/v1/teams',                                             label: 'List teams' },
];

// ── Types ─────────────────────────────────────────────────────────────────────

interface TestResult {
  id: string;
  status: 'pending' | 'running' | 'ok' | 'error' | 'skipped';
  statusCode?: number;
  latencyMs?: number;
  error?: string;
  body?: unknown;
  ts?: number;
}

// ── Helper ────────────────────────────────────────────────────────────────────

async function runEndpoint(ep: Endpoint): Promise<Omit<TestResult, 'id'>> {
  if (ep.skip) return { status: 'skipped' };

  const user = firebaseAuth.currentUser;
  const token = user ? await user.getIdToken() : null;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const t0 = performance.now();
  try {
    const res = await fetch(`${API_BASE}${ep.path}`, {
      method: ep.method,
      headers,
      body: ep.body ? JSON.stringify(ep.body) : undefined,
    });
    const latencyMs = Math.round(performance.now() - t0);
    let body: unknown;
    try { body = await res.json(); } catch { body = null; }

    return res.ok
      ? { status: 'ok', statusCode: res.status, latencyMs, body }
      : { status: 'error', statusCode: res.status, latencyMs, error: `HTTP ${res.status}`, body };
  } catch (e) {
    return { status: 'error', latencyMs: Math.round(performance.now() - t0), error: (e as Error).message };
  }
}

// ── Colour helpers ────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  pending:  { dot: '#475569', label: '#475569', bg: 'transparent' },
  running:  { dot: '#6366f1', label: '#6366f1', bg: '#6366f110' },
  ok:       { dot: '#10b981', label: '#34d399', bg: '#10b98110' },
  error:    { dot: '#ef4444', label: '#f87171', bg: '#ef444410' },
  skipped:  { dot: '#475569', label: '#64748b', bg: 'transparent' },
};

const METHOD_COLOR: Record<HttpMethod, string> = {
  GET:    '#34d399',
  POST:   '#818cf8',
  PATCH:  '#fbbf24',
  DELETE: '#f87171',
};

// ── Groups ────────────────────────────────────────────────────────────────────

const GROUPS = Array.from(new Set(ENDPOINTS.map(e => e.group)));

// ── Main component ────────────────────────────────────────────────────────────

export default function ApiTesterPage() {
  const [results, setResults]       = useState<Record<string, TestResult>>({});
  const [running, setRunning]       = useState(false);
  const [expanded, setExpanded]     = useState<Set<string>>(new Set());
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set(GROUPS));
  const abortRef = useRef(false);

  const setResult = (id: string, patch: Partial<Omit<TestResult, 'id'>>) =>
    setResults(prev => {
      const base: TestResult = prev[id] ?? { id, status: 'pending' };
      return { ...prev, [id]: { ...base, ...patch, id } };
    });


  async function runAll() {
    abortRef.current = false;
    setRunning(true);
    ENDPOINTS.forEach(ep => setResult(ep.id, { status: 'running' }));

    for (const ep of ENDPOINTS) {
      if (abortRef.current) break;
      setResult(ep.id, { status: 'running' });
      const res = await runEndpoint(ep);
      setResult(ep.id, { ...res, ts: Date.now() });
    }
    setRunning(false);
  }

  async function runOne(ep: Endpoint) {
    setResult(ep.id, { status: 'running' });
    const res = await runEndpoint(ep);
    setResult(ep.id, { ...res, ts: Date.now() });
  }

  const counts = {
    ok:    Object.values(results).filter(r => r.status === 'ok').length,
    error: Object.values(results).filter(r => r.status === 'error').length,
    total: ENDPOINTS.length,
  };

  const toggleGroup = (g: string) =>
    setOpenGroups(prev => { const n = new Set(prev); if (n.has(g)) n.delete(g); else n.add(g); return n; });
  const toggleExpand = (id: string) =>
    setExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });


  return (
    <div className="page">
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>API Endpoint Tester</h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            Verify all {counts.total} backend endpoints are reachable and responding correctly
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {running && (
            <button onClick={() => { abortRef.current = true; }}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', border: '1px solid #ef4444', background: '#ef444415', color: '#f87171', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Stop
            </button>
          )}
          <button
            id="run-all-tests-btn"
            onClick={runAll}
            disabled={running}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: running ? '#6366f150' : 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: running ? 'not-allowed' : 'pointer', boxShadow: running ? 'none' : '0 2px 12px var(--color-primary-glow)' }}
          >
            {running ? <Loader2 size={15} style={{ animation: 'spin 0.75s linear infinite' }} /> : <Play size={15} fill="currentColor" />}
            {running ? 'Running…' : 'Run All Tests'}
          </button>
        </div>
      </div>

      {/* ── Summary bar ── */}
      {Object.keys(results).length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { label: `${counts.ok} passed`, color: '#34d399', bg: '#10b98115', border: '#10b98130' },
            { label: `${counts.error} failed`, color: '#f87171', bg: '#ef444415', border: '#ef444430' },
            { label: `${counts.total - counts.ok - counts.error} pending`, color: '#64748b', bg: '#47556915', border: '#47556930' },
          ].map(s => (
            <div key={s.label} style={{ padding: '6px 14px', borderRadius: 8, background: s.bg, border: `1px solid ${s.border}`, fontSize: 13, fontWeight: 600, color: s.color }}>
              {s.label}
            </div>
          ))}
          {counts.ok === counts.total && !running && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, background: '#10b98115', border: '1px solid #10b98130', fontSize: 13, fontWeight: 700, color: '#34d399' }}>
              <CheckCircle2 size={14} /> All systems operational
            </div>
          )}
        </div>
      )}

      {/* ── Grouped list ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {GROUPS.map(group => {
          const groupEps = ENDPOINTS.filter(e => e.group === group);
          const isOpen = openGroups.has(group);
          const groupOk    = groupEps.filter(e => results[e.id]?.status === 'ok').length;
          const groupError = groupEps.filter(e => results[e.id]?.status === 'error').length;

          return (
            <div key={group} style={{ border: '1px solid #1a1f35', borderRadius: 12, overflow: 'hidden' }}>
              {/* group header */}
              <button
                onClick={() => toggleGroup(group)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#0a0e1c', border: 'none', cursor: 'pointer', textAlign: 'left' }}
              >
                {isOpen ? <ChevronDown size={14} style={{ color: '#475569', flexShrink: 0 }} /> : <ChevronRight size={14} style={{ color: '#475569', flexShrink: 0 }} />}
                <span style={{ fontWeight: 700, fontSize: 13, color: '#94a3b8', flex: 1 }}>{group}</span>
                <span style={{ fontSize: 11, color: '#475569' }}>{groupEps.length} endpoints</span>
                {groupOk > 0 && <span style={{ fontSize: 11, color: '#34d399', fontWeight: 600 }}>{groupOk} ✓</span>}
                {groupError > 0 && <span style={{ fontSize: 11, color: '#f87171', fontWeight: 600 }}>{groupError} ✗</span>}
              </button>

              {isOpen && (
                <div style={{ borderTop: '1px solid #1a1f35' }}>
                  {groupEps.map((ep, i) => {
                    const result = results[ep.id];
                    const sc = STATUS_COLOR[result?.status ?? 'pending'];
                    const isExpanded = expanded.has(ep.id);

                    return (
                      <div key={ep.id} style={{ borderTop: i > 0 ? '1px solid #12172a' : undefined }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px' }}>
                          {/* Status dot */}
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: sc.dot, flexShrink: 0, ...(result?.status === 'running' ? { animation: 'pulse 1s infinite' } : {}) }} />

                          {/* Method badge */}
                          <span style={{ fontSize: 11, fontWeight: 800, color: METHOD_COLOR[ep.method], background: `${METHOD_COLOR[ep.method]}18`, padding: '2px 7px', borderRadius: 5, fontFamily: 'monospace', flexShrink: 0 }}>
                            {ep.method}
                          </span>

                          {/* Path */}
                          <code style={{ fontSize: 12, color: '#94a3b8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ep.path}</code>

                          {/* Label */}
                          <span style={{ fontSize: 12, color: '#475569', flexShrink: 0 }}>{ep.label}</span>

                          {/* Latency */}
                          {result?.latencyMs !== undefined && (
                            <span style={{ fontSize: 11, color: result.latencyMs < 300 ? '#34d399' : result.latencyMs < 1000 ? '#fbbf24' : '#f87171', fontWeight: 600, flexShrink: 0 }}>
                              {result.latencyMs}ms
                            </span>
                          )}

                          {/* Status code */}
                          {result?.statusCode !== undefined && (
                            <span style={{ fontSize: 11, color: result.status === 'ok' ? '#34d399' : '#f87171', fontWeight: 700, flexShrink: 0 }}>
                              {result.statusCode}
                            </span>
                          )}

                          {/* Actions */}
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <button
                              id={`test-${ep.id}`}
                              onClick={() => runOne(ep)}
                              disabled={result?.status === 'running'}
                              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', border: '1px solid #1e2130', borderRadius: 6, background: 'transparent', color: '#64748b', fontSize: 11, cursor: 'pointer' }}
                              title="Run this test"
                            >
                              {result?.status === 'running' ? <Loader2 size={11} style={{ animation: 'spin 0.75s linear infinite' }} /> : <RefreshCw size={11} />} Run
                            </button>
                            {result && result.status !== 'pending' && (
                              <button
                                onClick={() => toggleExpand(ep.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', border: '1px solid #1e2130', borderRadius: 6, background: 'transparent', color: '#64748b', fontSize: 11, cursor: 'pointer' }}
                              >
                                {isExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />} Response
                              </button>
                            )}
                          </div>

                          {/* Status icon */}
                          {result?.status === 'ok'    && <CheckCircle2 size={16} style={{ color: '#34d399', flexShrink: 0 }} />}
                          {result?.status === 'error' && <XCircle      size={16} style={{ color: '#f87171', flexShrink: 0 }} />}
                          {result?.status === 'skipped' && <AlertTriangle size={16} style={{ color: '#fbbf24', flexShrink: 0 }} />}
                        </div>

                        {/* Expanded response */}
                        {isExpanded && result && (
                          <div style={{ padding: '0 16px 12px 40px' }}>
                            {result.error && (
                              <div style={{ padding: '8px 12px', borderRadius: 8, background: '#ef444410', border: '1px solid #ef444430', color: '#f87171', fontSize: 12, marginBottom: 8 }}>
                                {result.error}
                              </div>
                            )}
                            {result.body !== undefined && result.body !== null && (
                              <pre style={{ margin: 0, fontSize: 11, color: '#94a3b8', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, padding: '10px 14px', overflowX: 'auto', maxHeight: 260, overflowY: 'auto', lineHeight: 1.5 }}>
                                {JSON.stringify(result.body, null, 2).slice(0, 4000)}{JSON.stringify(result.body, null, 2).length > 4000 ? '\n… (truncated)' : ''}
                              </pre>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
      `}</style>
    </div>
  );
}
